/**
 * Demo data galerie — KONVENCE (ustava kitu):
 *  - skutecna data: dlouhe ceske adresy, klient bez prijmeni, diakritika,
 *    tisice radku — komponenta, ktera prezije tohle, prezije produkci;
 *  - deterministicky generator (seedovany PRNG, zadny Math.random/new Date),
 *    aby Playwright screenshoty byly stabilni vizualni regresi.
 */

import type { ListQuery, ListResult } from '@redhand/ui';

export interface DemoProperty {
  readonly id: string;
  readonly name: string;
  readonly client: string;
  readonly status: string;
  readonly price: string;
}

/** mulberry32 — maly deterministicky PRNG. */
function mulberry32(seed: number): () => number {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const STREETS = [
  'Náměstí Přemysla Otakara II. 1234/56, České Budějovice 370 01',
  'Dlouhá třída Maršála Koněva 987/12a, Havířov-Podlesí 736 01',
  'U Staré vodárenské věže 3, Praha 6 — Bubeneč 160 00',
  'Krátká 8, Řevnice 252 30',
  'Sídliště Pod Hůrkou 1502/47, Klatovy III 339 01',
  'Třída Tomáše Garrigua Masaryka 2210/118, Karlovy Vary 360 01',
];

const CLIENTS = [
  'Miroslava', // klient bez prijmeni — realny pripad, UI nesmi spadnout
  'Ing. arch. Květoslava Vondráčková-Šťastná, Ph.D.',
  'Jan Novák',
  'Đorđe Kovačević',
  'SPOLEČENSTVÍ VLASTNÍKŮ JEDNOTEK domu čp. 1502, Klatovy',
  'Chù Nguyễn Thị',
];

const TYPES = ['Byt 3+kk', 'Byt 2+1', 'Rodinný dům 5+1', 'Pozemek 1 240 m²', 'Kancelář 86 m²', 'Chata u přehrady'];

export const STATUSES = ['Nábor', 'Publikováno', 'Rezervace', 'Prodáno', 'Pozastaveno'] as const;

function pick<T>(arr: readonly T[], rnd: () => number): T {
  const item = arr[Math.floor(rnd() * arr.length)];
  if (item === undefined) throw new Error('empty demo pool');
  return item;
}

function czk(rnd: () => number): string {
  const value = (Math.floor(rnd() * 1900) + 90) * 10_000;
  return `${new Intl.NumberFormat('cs-CZ').format(value)} Kč`;
}

/** 412 deterministickych radku — dost na test strankovani i vykonu. */
export function demoProperties(count = 412): readonly DemoProperty[] {
  const rnd = mulberry32(20260829);
  return Array.from({ length: count }, (_, i) => ({
    id: `P-${String(i + 1).padStart(4, '0')}`,
    name: `${pick(TYPES, rnd)}, ${pick(STREETS, rnd)}`,
    client: pick(CLIENTS, rnd),
    status: pick(STATUSES, rnd),
    price: czk(rnd),
  }));
}

/** Lokalni implementace LIST-CONTRACT.md nad demo daty (galerie nema server). */
export function queryDemoProperties(all: readonly DemoProperty[], q: ListQuery): ListResult<DemoProperty> {
  let rows = [...all];
  const statusFilter = q.filters?.['status'];
  if (typeof statusFilter === 'string' && statusFilter !== '') {
    rows = rows.filter((r) => r.status === statusFilter);
  }
  if (q.search !== undefined && q.search !== '') {
    const needle = q.search.toLocaleLowerCase('cs');
    rows = rows.filter(
      (r) => r.name.toLocaleLowerCase('cs').includes(needle) || r.client.toLocaleLowerCase('cs').includes(needle),
    );
  }
  const sort = q.sort?.[0];
  if (sort) {
    const dir = sort.dir === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      const av = String((a as unknown as Record<string, unknown>)[sort.field] ?? '');
      const bv = String((b as unknown as Record<string, unknown>)[sort.field] ?? '');
      return av.localeCompare(bv, 'cs') * dir;
    });
  }
  const start = (q.page - 1) * q.pageSize;
  return {
    items: rows.slice(start, start + q.pageSize),
    total: rows.length,
    page: q.page,
    pageSize: q.pageSize,
  };
}

export interface DemoTask {
  readonly id: string;
  readonly title: string;
  readonly context: string;
  readonly due: string;
}

export const demoTasks: readonly DemoTask[] = [
  { id: 't1', title: 'Zavolat Miroslavě — potvrzení prohlídky', context: 'Byt 3+kk, Náměstí Přemysla Otakara II. 1234/56', due: 'dnes 10:00' },
  { id: 't2', title: 'Doplnit energetický štítek do inzerátu', context: 'Rodinný dům 5+1, Krátká 8, Řevnice', due: 'dnes 12:00' },
  { id: 't3', title: 'Připravit rezervační smlouvu k podpisu', context: 'Pozemek 1 240 m², Hluboká nad Vltavou', due: 'dnes 15:30' },
  { id: 't4', title: 'Nahrát fotky z focení do karty nemovitosti', context: 'Kancelář 86 m², Karlovy Vary', due: 'zítra 9:00' },
];

export const demoTimeline = [
  { id: 'e1', time: '29. 8. 2026 14:05', title: 'E-mail: potvrzení rezervace odesláno', kind: 'email' as const, detail: 'příjemce: klient@example.cz, šablona rezervace-potvrzeni' },
  { id: 'e2', time: '29. 8. 2026 13:58', title: 'Stav změněn: Publikováno → Rezervace', kind: 'status' as const },
  { id: 'e3', time: '28. 8. 2026 16:12', title: 'Poznámka makléře', kind: 'note' as const, detail: 'Kupující chce termín podpisu do konce září, ověřit financování.' },
  { id: 'e4', time: '25. 8. 2026 9:00', title: 'Stav změněn: Nábor → Publikováno', kind: 'status' as const },
];

export const demoAttention = [
  {
    id: 'a1',
    severity: 'critical' as const,
    title: 'Rezervační záloha po splatnosti 5 dní',
    detail: 'Pozemek 1 240 m², Hluboká nad Vltavou — kupující Đorđe Kovačević',
  },
  {
    id: 'a2',
    severity: 'warning' as const,
    title: 'Inzerát 30 dní bez poptávky',
    detail: 'Byt 2+1, Dlouhá třída Maršála Koněva 987/12a, Havířov-Podlesí',
  },
  {
    id: 'a3',
    severity: 'info' as const,
    title: 'Končí platnost energetického štítku',
    detail: 'Rodinný dům 5+1, Krátká 8, Řevnice — platnost do 30. 9. 2026',
  },
];

export const demoWeekSales = [
  { day: 'Po', current: 12400, previous: 9800 },
  { day: 'Út', current: 18100, previous: 14300 },
  { day: 'St', current: 15600, previous: 16900 },
  { day: 'Čt', current: 21800, previous: 17200 },
  { day: 'Pá', current: 19400, previous: 20100 },
  { day: 'So', current: 24800, previous: 18600 },
  { day: 'Ne', current: 16200, previous: 15400 },
];

export const demoOrderStates = [
  { key: 'new', label: 'Nová', value: 34 },
  { key: 'paid', label: 'Zaplacená', value: 128 },
  { key: 'shipped', label: 'Odeslaná', value: 61 },
  { key: 'delivered', label: 'Doručená', value: 204 },
  { key: 'returned', label: 'Vrácená', value: 9 },
];
